import React, { useState } from 'react';
import axios from 'axios';
import { Formik } from 'formik';
import * as Yup from 'yup';

import CardDialog from '~/components/CardDialog';
import { Caption, FsButton, FsIconButton, Heading, LoadingSwitch, Text } from '~/components/core';
import Button from '~/components/core/Atomic/Buttons/Button';
import CancelButton from '~/components/core/Buttons/CancelButton';
import StatusChipCore from '~/components/core/StatusChipCore';
import { CopyToClipboardIcon, PencilIcon } from '~/components/icons';
import CheckIcon from '~/components/icons/CheckIcon';
import WarningCircleIcon from '~/components/icons/WarningCircle';
import Detail from '~/components/SystemConfiguration/Integrations/OneInc/InternalAdmin/Detail';
import { useSysconfig } from '~/components/SystemConfiguration/SystemConfigurationScreen';
import TextFieldFormik from '~/components/TextFieldFormik';
import type { OrganizationModel } from '~/components/types/organization-types';
import useDataFetcher from '~/components/useDataFetcher';
import type {
  OneIncInboundTenantConfigInfo,
  UpdateOneIncInboundTenantConfigParams,
} from '~/server_shared/openapi/data-contracts';
import { reportAxiosError } from '~/Utils';

import { useStyles } from '../../../../../../assets/styles';
import styles from '../oneIncInternalAdmin.module.scss';

// The same placeholder the server treats as "leave the stored key alone"
// (server.utils.DEFAULT_SECRET_TEMPLATE). Declared locally, as the scheduled-export dialogs do.
const DEFAULT_SECRET_TEMPLATE = '*****';

interface InboundPaymentsFormDialogProps {
  onSubmit: (values: UpdateOneIncInboundTenantConfigParams) => Promise<void>;
  onClose: () => void;
  configuration?: OneIncInboundTenantConfigInfo;
}

const InboundPaymentsFormDialog: React.FC<InboundPaymentsFormDialogProps> = ({ onSubmit, onClose, configuration }) => {
  const { classes } = useStyles();
  const isConfigured = !!configuration?.has_authentication_key && !!configuration?.has_ui_authentication_key;

  return (
    <Formik<UpdateOneIncInboundTenantConfigParams>
      initialValues={{
        base_url: configuration?.base_url || '',
        // Prefilled with the placeholder once a key is stored, so submitting the form after
        // changing only the base URL preserves credentials the screen never showed.
        authentication_key: configuration?.has_authentication_key ? DEFAULT_SECRET_TEMPLATE : '',
        ui_authentication_key: configuration?.has_ui_authentication_key ? DEFAULT_SECRET_TEMPLATE : '',
        webhook_signing_key: configuration?.has_webhook_signing_key ? DEFAULT_SECRET_TEMPLATE : '',
      }}
      validationSchema={Yup.object().shape({
        base_url: Yup.string().url('Must be a valid URL').required('Required'),
        // Required only on the first save: after that the placeholder stands in for a stored key,
        // and the server rejects a first write that carries neither.
        authentication_key: isConfigured ? Yup.string() : Yup.string().required('Required'),
        ui_authentication_key: isConfigured ? Yup.string() : Yup.string().required('Required'),
        // Never required, unlike the two above: One Inc Support issues the webhook signing key
        // separately, so a tenant is legitimately saved without one and can already send. The
        // server applies the same rule.
        webhook_signing_key: Yup.string(),
      })}
      onSubmit={async (values, { setSubmitting }) => {
        try {
          await onSubmit(values);
        } catch {
          setSubmitting(false);
        }
      }}
    >
      {({ isSubmitting, handleSubmit }) => (
        <CardDialog
          title="One Inc Inbound Payments"
          isDialog
          preventClose={isSubmitting}
          maxWidth="sm"
          fullWidth
          onClose={onClose}
        >
          <div className={styles.inputsContainer}>
            <TextFieldFormik id="base_url" label="Base URL" className={classes.textField} fullWidth />
            <TextFieldFormik
              id="authentication_key"
              label="Authentication key"
              // @ts-expect-error type is not defined in TextFieldFormik
              type="password"
              className={classes.textField}
              fullWidth
            />
            <TextFieldFormik
              id="ui_authentication_key"
              label="UI authentication key"
              // @ts-expect-error type is not defined in TextFieldFormik
              type="password"
              className={classes.textField}
              fullWidth
            />
            <TextFieldFormik
              id="webhook_signing_key"
              label="Webhook signing key"
              // @ts-expect-error type is not defined in TextFieldFormik
              type="password"
              className={classes.textField}
              fullWidth
            />
          </div>
          <div className={classes.buttonsContainer}>
            <CancelButton disabled={isSubmitting} onClick={onClose} />
            <Button variant="contained" color="primary" disabled={isSubmitting} onClick={() => handleSubmit()}>
              Save
            </Button>
          </div>
        </CardDialog>
      )}
    </Formik>
  );
};

// No reveal control here, unlike the ClaimsPay card above it: neither key is ever sent to the
// browser, so "Configured" is the whole of what this screen can know. PremiumPay is also a separate
// tenant sharing no credential with ClaimsPay, hence its own fetch and its own dialog.
const InboundPaymentsCard: React.FC = () => {
  const [isFormOpen, setIsFormOpen] = useState(false);
  const { organization } = useSysconfig() as { organization: OrganizationModel };
  const url = `/sysconfig/api/v1/organizations/${organization.id}/one_inc_inbound/config`;
  const { isLoading, isError, data, reloadData } = useDataFetcher<OneIncInboundTenantConfigInfo>(url);

  const isConfigured = !!data?.has_authentication_key && !!data?.has_ui_authentication_key;

  const handleSubmit = async (values: UpdateOneIncInboundTenantConfigParams) => {
    try {
      await axios.put(url, values);
      await reloadData();
      setIsFormOpen(false);
    } catch (error) {
      await reportAxiosError(error);
      throw error;
    }
  };

  return (
    <LoadingSwitch isLoading={isLoading} isError={isError}>
      <div className={styles.containerWithBorder}>
        <div className={styles.sectionTitle}>
          <Heading variant={Heading.TYPES.H4}>Inbound Payments (PremiumPay)</Heading>
          {isConfigured ? (
            <FsIconButton aria-label="Edit inbound payments" icon={PencilIcon} onClick={() => setIsFormOpen(true)} />
          ) : null}
        </div>
        {isConfigured ? (
          <>
            {/* Split send-side from receive-side rather than one flat row of four. The signing key
                being absent while the three above are set is a normal state, not a half-finished
                one, and the grouping is what says so without a sentence. */}
            <div className={styles.sectionDetails}>
              <Detail label="Base URL" text={data?.base_url || ''} />
              <Detail label="Authentication key">
                <StatusChipCore status="Configured" Icon={CheckIcon} color="green" />
              </Detail>
              <Detail label="UI authentication key">
                <StatusChipCore status="Configured" Icon={CheckIcon} color="green" />
              </Detail>
            </div>
            <div className={styles.sectionDetails}>
              {/* The endpoint an administrator has to hand to One Inc Support: they register it and
                  only then issue the signing key below, so this is the first step of the two and
                  copying it is the whole interaction. */}
              <Detail label="Webhook URL">
                <div className={styles.copyableValue}>
                  <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.REGULAR}>
                    {data?.webhook_url || ''}
                  </Text>
                  <FsIconButton
                    aria-label="Copy webhook URL"
                    icon={CopyToClipboardIcon}
                    onClick={() => navigator.clipboard.writeText(data?.webhook_url || '')}
                  />
                </div>
              </Detail>
              {/* Reported separately rather than folded into `isConfigured`: the webhook signing key
                  is issued by One Inc Support on its own schedule and is not needed to send, so an
                  organization can be sending while unable to authenticate the acknowledgement that
                  comes back. Actionable, so it carries what it costs -- an acknowledgement that is
                  rejected is a payer's money taken and never recorded on the claim, which "Not
                  configured" on its own does not convey. */}
              <Detail
                label="Webhook signing key"
                helperText={
                  data?.has_webhook_signing_key ? undefined : 'Inbound payment acknowledgements will be rejected'
                }
              >
                {data?.has_webhook_signing_key ? (
                  <StatusChipCore status="Configured" Icon={CheckIcon} color="green" />
                ) : (
                  <StatusChipCore status="Not configured" Icon={WarningCircleIcon} color="orange" />
                )}
              </Detail>
            </div>
          </>
        ) : (
          <>
            {/* The dialog behind this button asks for a base URL and two GUIDs that only One Inc
                Support can issue, so saying so here is what stops a first-time administrator opening
                it, meeting four empty required fields and leaving. */}
            <div className={styles.emptyStateDescription}>
              <Caption variant={Caption.VARIANTS.LABEL}>
                Email payers a link to pay, and record what they pay against the claim. You will need the PortalOne base
                URL and authentication keys from One Inc Support.
              </Caption>
            </div>
            <FsButton color="primary" variant="contained" className={styles.button} onClick={() => setIsFormOpen(true)}>
              Enable Inbound Payments
            </FsButton>
          </>
        )}
      </div>
      {isFormOpen ? (
        <InboundPaymentsFormDialog onSubmit={handleSubmit} configuration={data} onClose={() => setIsFormOpen(false)} />
      ) : null}
    </LoadingSwitch>
  );
};

export default InboundPaymentsCard;
